import { useCallback, useEffect, useState } from 'react';
import { Card, StatusBadge, Modal, PageHeader, DataTable, EmptyState, Tabs } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { KpiStrip, type Kpi } from './reports/KpiStrip';
import { GrowBar } from './reports/GrowBar';
import { CountUp } from '@/components/ui/CountUp';

interface Plan {
  id: number;
  name: string;
  monthly_price_cents: number;
  currency?: string;
  minutes_included: number;
  max_agents: number;
}

interface BillingData {
  subscription?: {
    id: number;
    status: string;
    current_period_end: string;
    plan?: Plan;
    plan_id?: number;
  } | null;
  minutes_used: number;
  usage_by_month?: { month: string; minutes: number; calls: number }[];
  charge?: {
    month: string; users: number; minutes: number;
    price_per_user_cents: number; price_per_minute_cents: number;
    users_cost_cents: number; minutes_cost_cents: number; total_cents: number;
    dids_count?: number; dids_monthly_cost_cents?: number; dids_purchase_cost_cents?: number;
    ai_transcript_minutes?: number; ai_transcript_cost_cents?: number;
    ai_summary_calls?: number; ai_summary_cost_cents?: number;
    currency: string; pricing_configured: boolean;
  } | null;
  invoices: { id: number; number: string; period: string; amount_cents: number; currency?: string; status: string; due_at?: string; pdf_url?: string | null }[];
  available_plans: Plan[];
}

// הצגת "חבילות זמינות" (כולל כפתור מעבר-חבילה). הוסתר לבקשת דורון
// 17/08/2026 — "כרגע"; להחזרה: true. המודאל וההחלפה נשארים בקוד.
const SHOW_AVAILABLE_PLANS = false;

const SUB_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  active: { label: 'פעיל', tone: 'ok' },
  trial: { label: 'ניסיון', tone: 'warn' },
  past_due: { label: 'באיחור', tone: 'danger' },
  cancelled: { label: 'בוטל', tone: 'neutral' },
};

const INV_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  paid: { label: 'שולם', tone: 'ok' },
  open: { label: 'פתוח', tone: 'warn' },
  overdue: { label: 'באיחור', tone: 'danger' },
  void: { label: 'בוטל', tone: 'neutral' },
};

function money(cents: number, currency = 'ILS'): string {
  return (cents / 100).toLocaleString('he-IL', { style: 'currency', currency, maximumFractionDigits: 2 });
}

// 28/09/2026 (GoCall v2): המסך מחולק ללשוניות כמו בעיצוב. "אמצעי תשלום"
// ו"היסטוריה" שבעיצוב לא נבנו — אין להם נתונים ב-/admin/billing.
type BillingTab = 'account' | 'contract' | 'usage' | 'invoices';
const TABS: { id: BillingTab; label: string }[] = [
  { id: 'account', label: 'מצב חשבון' },
  { id: 'contract', label: 'החוזה שלי' },
  { id: 'usage', label: 'שימוש' },
  { id: 'invoices', label: 'חשבוניות' },
];

/** "משבצת" בנוסחת החיוב: ערך מודגש + תווית קטנה (הסכום הסופי כהה). */
function FormulaChip({ value, label, dark, i = 0 }: { value: string; label: string; dark?: boolean; i?: number }) {
  return (
    <span className="anim-up" style={{
      ['--d' as string]: `${0.15 + i * 0.07}s`,
      display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px', borderRadius: 12,
      background: dark ? 'var(--text)' : 'var(--surface-3)',
    }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: dark ? 'var(--bg)' : 'var(--text)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
      <span style={{ fontSize: 'var(--t-xs)', color: dark ? 'var(--faint)' : 'var(--muted)' }}>{label}</span>
    </span>
  );
}

// משבצות הנוסחה והסימנים ביניהן נכנסים מדורגים (i = מיקום בשורה).
const Op = ({ c, i = 0 }: { c: string; i?: number }) => (
  <span aria-hidden="true" className="anim-fade" style={{ color: 'var(--muted)', fontSize: 18, ['--d' as string]: `${0.15 + i * 0.07}s` }}>{c}</span>
);

/** שורת "שדה: ערך" ברשימת פרטי החוזה. */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 220px) minmax(0, 1fr)', gap: 16, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--line)', fontSize: 13.5 }}>
      <span style={{ color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{children}</span>
    </div>
  );
}

export function Billing() {
  const [tab, setTab] = useState<BillingTab>('account');
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [switchTarget, setSwitchTarget] = useState<Plan | null>(null);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await adminApi.billing()) as BillingData);
    } catch {
      toast('שגיאה בטעינת נתוני חיוב', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function doSwitch(plan: Plan) {
    setSwitching(true);
    try {
      await adminApi.changePlan(plan.id);
      toast(`עברת לחבילת ${plan.name}`, 'ok');
      setSwitchTarget(null);
      void load();
    } catch {
      toast('שגיאה בהחלפת חבילה', 'danger');
    } finally {
      setSwitching(false);
    }
  }

  if (loading) return <div style={{ padding: 'var(--gap)' }}><Card>טוען…</Card></div>;
  if (!data?.available_plans || !data?.invoices) return <div style={{ padding: 'var(--gap)' }}><Card>שגיאה בטעינת נתוני חיוב.</Card></div>;

  const sub = data.subscription;
  const currentPlanId = sub?.plan?.id ?? sub?.plan_id;
  const included = sub?.plan?.minutes_included ?? 0;
  const used = data.minutes_used ?? 0;

  const usageCols: DataTableColumn<{ month: string; minutes: number; calls: number }>[] = [
    { key: 'month', he: 'חודש', render: (m) => <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{m.month}</span> },
    { key: 'minutes', he: 'דקות שיחה', render: (m) => m.minutes.toLocaleString('he-IL') },
    { key: 'calls', he: 'שיחות', render: (m) => m.calls.toLocaleString('he-IL') },
  ];

  const invoiceCols: DataTableColumn<BillingData['invoices'][number]>[] = [
    { key: 'number', he: 'מספר', render: (inv) => <span dir="ltr">{inv.number}</span> },
    { key: 'period', he: 'תקופה', render: (inv) => inv.period },
    { key: 'amount', he: 'סכום', render: (inv) => money(inv.amount_cents, inv.currency) },
    {
      key: 'status', he: 'סטטוס',
      render: (inv) => (
        <StatusBadge tone={INV_STATUS[inv.status]?.tone ?? 'neutral'} size="sm" dot>
          {INV_STATUS[inv.status]?.label ?? inv.status}
        </StatusBadge>
      ),
    },
    {
      key: '_pdf', he: '',
      render: (inv) => inv.pdf_url
        ? <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="btn ghost">PDF</a>
        : <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>—</span>,
    },
  ];

  const charge = data.charge?.pricing_configured ? data.charge : null;
  const cur = charge?.currency ?? 'ILS';
  const aiCents = (charge?.ai_transcript_cost_cents ?? 0) + (charge?.ai_summary_cost_cents ?? 0);
  const monthLabel = (ym?: string) => {
    if (!ym) return '';
    const d = new Date(`${ym.slice(0, 7)}-01T00:00:00`);
    return Number.isNaN(d.getTime()) ? ym : d.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
  };
  const usedPct = included > 0 ? Math.round((used / included) * 100) : 0;

  const kpi = (label: string, n: number): Kpi => ({ label, value: n.toLocaleString('he-IL'), num: n });
  const usageKpis: Kpi[] = [
    kpi('דקות שיחה בפועל', charge?.minutes ?? used),
    ...(charge ? [kpi('משתמשים פעילים', charge.users)] : []),
    ...(data.usage_by_month?.[0] ? [kpi(`שיחות · ${monthLabel(data.usage_by_month[0].month)}`, data.usage_by_month[0].calls)] : []),
    ...(charge ? [kpi('מספרים', charge.dids_count ?? 0)] : []),
    ...(charge && (charge.ai_transcript_minutes ?? 0) > 0 ? [kpi('דקות תמלול AI', charge.ai_transcript_minutes ?? 0)] : []),
  ];

  return (
    <div className="stack page-enter" style={{ padding: '14px 16px 40px', maxWidth: 1280, width: '100%' }}>
      <PageHeader title="פיננסים" />

      <Tabs items={TABS} value={tab} onChange={(id) => setTab(id as BillingTab)} />

      {tab === 'account' && (
        <div className="card" style={{ padding: 'clamp(20px, 3vw, 30px) clamp(18px, 3vw, 32px)', display: 'flex', flexDirection: 'column', gap: 26 }}>
          {charge ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>כמה אני חייב עכשיו · {monthLabel(charge.month)}, לפי שימוש בפועל</span>
                <span style={{ fontSize: 'clamp(48px, 8vw, 84px)', fontWeight: 300, lineHeight: 0.95, letterSpacing: -3, fontVariantNumeric: 'tabular-nums' }}>
                  <CountUp value={charge.total_cents} format={(v) => money(Math.round(v), cur)} />
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <FormulaChip i={0} value={`${charge.users} נציגים × ${money(charge.price_per_user_cents, cur)}`} label={`משתמשים · ${money(charge.users_cost_cents, cur)}`} />
                <Op c="+" i={1} />
                <FormulaChip i={2} value={`${charge.minutes.toLocaleString('he-IL')} דקות × ${money(charge.price_per_minute_cents, cur)}`} label={`דקות שנוצלו · ${money(charge.minutes_cost_cents, cur)}`} />
                <Op c="+" i={3} />
                <FormulaChip
                  i={4}
                  value={money(charge.dids_monthly_cost_cents ?? 0, cur)}
                  label={`מספרים (${charge.dids_count ?? 0})${(charge.dids_purchase_cost_cents ?? 0) > 0 ? ` · + ${money(charge.dids_purchase_cost_cents ?? 0, cur)} קניית מספרים החודש` : ''}`}
                />
                {aiCents > 0 && (
                  <>
                    <Op c="+" i={5} />
                    <FormulaChip i={6} value={money(aiCents, cur)} label={`AI לשיחות (${charge.ai_transcript_minutes ?? 0} דק' תמלול, ${charge.ai_summary_calls ?? 0} סיכומים)`} />
                  </>
                )}
                <Op c="=" i={aiCents > 0 ? 7 : 5} />
                <FormulaChip i={aiCents > 0 ? 8 : 6} value={money(charge.total_cents, cur)} label="סה״כ לחיוב" dark />
              </div>
            </>
          ) : (
            <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>תמחור לפי שימוש לא הוגדר לחשבון — החיוב לפי החבילה.</span>
          )}

          {sub ? (
            <>
              {charge && <div style={{ height: 1, background: 'var(--line)' }} />}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    דקות מנוצלות (החודש) · {sub.plan?.name ?? '—'}
                    <StatusBadge tone={SUB_STATUS[sub.status]?.tone ?? 'neutral'} size="sm" dot>{SUB_STATUS[sub.status]?.label ?? sub.status}</StatusBadge>
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }} dir="ltr">
                    {used.toLocaleString('he-IL')} / {included ? included.toLocaleString('he-IL') : '∞'}{included > 0 ? ` · ${usedPct}%` : ''}
                  </span>
                </div>
                {included > 0 && (
                  <div role="progressbar" aria-valuenow={Math.min(used, included)} aria-valuemax={included} aria-label="דקות מנוצלות" style={{ display: 'flex' }}>
                    <GrowBar pct={usedPct} height={12} ms={1000} color={used > included ? 'var(--danger)' : 'var(--accent)'} />
                  </div>
                )}
                {included > 0 && used > included && (
                  <div style={{ color: 'var(--danger)', fontSize: 'var(--t-xs)' }}>
                    חריגה של {(used - included).toLocaleString('he-IL')} דקות
                  </div>
                )}
              </div>
            </>
          ) : (
            <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין מנוי פעיל.</span>
          )}
        </div>
      )}

      {tab === 'contract' && (
        <>
          <div className="card" style={{ padding: '14px 28px 18px', maxWidth: 860 }}>
            {sub ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldRow label="חבילה">{sub.plan?.name ?? '—'}</FieldRow>
                <FieldRow label="סטטוס">
                  <StatusBadge tone={SUB_STATUS[sub.status]?.tone ?? 'neutral'} size="sm" dot>{SUB_STATUS[sub.status]?.label ?? sub.status}</StatusBadge>
                </FieldRow>
                <FieldRow label="מתחדש בתאריך">{new Date(sub.current_period_end).toLocaleDateString('he-IL')}</FieldRow>
                {sub.plan && <FieldRow label="דקות כלולות">{sub.plan.minutes_included ? sub.plan.minutes_included.toLocaleString('he-IL') : '∞'}</FieldRow>}
                {sub.plan && <FieldRow label="נציגים">עד {sub.plan.max_agents}</FieldRow>}
                {charge && <FieldRow label="מחיר לנציג">{money(charge.price_per_user_cents, cur)} לחודש</FieldRow>}
                {charge && <FieldRow label="מחיר לדקה">{money(charge.price_per_minute_cents, cur)}</FieldRow>}
              </div>
            ) : (
              <div style={{ padding: '8px 0', color: 'var(--muted)' }}>אין מנוי פעיל. בחר חבילה למטה.</div>
            )}
          </div>

          {SHOW_AVAILABLE_PLANS && (
          <Card title="חבילות זמינות">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--gap)' }}>
              {data.available_plans.map((p) => {
                const isCurrent = p.id === currentPlanId;
                return (
                  <div
                    key={p.id}
                    style={{
                      padding: 'var(--pad-card)',
                      border: `2px solid ${isCurrent ? 'var(--accent)' : 'var(--line)'}`,
                      borderRadius: 'var(--r-3)',
                      background: isCurrent ? 'var(--surface-2)' : 'var(--surface)',
                    }}
                  >
                    <strong style={{ fontSize: 'var(--t-lg)' }}>{p.name}</strong>
                    <div style={{ fontSize: 'var(--t-2xl)', fontWeight: 700, color: 'var(--accent)', margin: '6px 0' }}>
                      {money(p.monthly_price_cents, p.currency)}
                      <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 400 }}> /חודש</span>
                    </div>
                    <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                      {p.minutes_included.toLocaleString('he-IL')} דקות · עד {p.max_agents} נציגים
                    </div>
                    {isCurrent ? (
                      <div style={{ marginTop: 10, textAlign: 'center', color: 'var(--accent)', fontWeight: 600, fontSize: 'var(--t-sm)' }}>
                        החבילה הנוכחית
                      </div>
                    ) : (
                      <button type="button" className="btn primary" style={{ marginTop: 10, width: '100%' }} onClick={() => setSwitchTarget(p)}>
                        עבור לחבילה זו
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
          )}

        </>
      )}

      {tab === 'usage' && (
        <>
          <KpiStrip items={usageKpis} ariaLabel="שימוש" />
          <Card title="דקות שיחה בפועל לפי חודש" padded={false} style={{ ['--d' as string]: '.1s' }}>
            <DataTable<{ month: string; minutes: number; calls: number }>
              columns={usageCols}
              rows={data.usage_by_month ?? []}
              emptyMessage="אין נתוני שימוש."
            />
          </Card>
        </>
      )}

      {tab === 'invoices' && (
        <Card padded={false} style={{ overflow: 'hidden' }}>
          <DataTable<BillingData['invoices'][number]>
            columns={invoiceCols}
            rows={data.invoices}
            caption="חשבוניות"
            emptyState={<EmptyState icon="doc" title="אין חשבוניות" description="חשבוניות שיופקו יופיעו כאן." />}
          />
        </Card>
      )}

      {switchTarget && (
        <Modal
          isOpen
          onClose={() => setSwitchTarget(null)}
          title="החלפת חבילה"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setSwitchTarget(null)} disabled={switching}>ביטול</button>
              <button type="button" className="btn primary" onClick={() => doSwitch(switchTarget)} disabled={switching}>
                {switching ? 'מחליף…' : 'אשר מעבר'}
              </button>
            </>
          }
        >
          <p>
            לעבור לחבילת <strong>{switchTarget.name}</strong> ({money(switchTarget.monthly_price_cents, switchTarget.currency)}/חודש)?
            השינוי ייכנס לתוקף מיידית.
          </p>
        </Modal>
      )}
    </div>
  );
}
